(() => {
  if (customElements.get('cart-drawer')) return;

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.panel = this.querySelector('.cart-drawer__panel');
      this.form = this.querySelector('form');
      this.message = this.querySelector('[data-cart-message]');
      this.cart = JSON.parse(this.querySelector('[data-cart-drawer-cart]').textContent);
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.refreshVersion = 0;
      this.busy = false;
      this.updateCount();
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.form.addEventListener('submit', (event) => {
        if (this.busy || !this.form.reportValidity()) event.preventDefault();
      }, { signal });
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-drawer-open], .js-drawer-open-cart');
        if (!opener || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        event.stopPropagation();
        this.open(opener);
        this.refresh();
      }, { signal, capture: true });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      document.addEventListener('focusin', (event) => {
        if (this.hasAttribute('open') && !this.panel.contains(event.target) && !window.CartDrawerRedo?.isInfoDialogOpen()) {
          this.querySelector('.cart-drawer__close').focus({ preventScroll: true });
        }
      }, { signal });
      document.addEventListener('modalOpen', () => this.close(), { signal });
      document.addEventListener('ajaxProduct:added', (event) => {
        if (event.detail?.product?.status >= 400) return;
        this.open(event.detail?.addToCartBtn || document.activeElement);
        this.refresh();
      }, { signal });
      document.addEventListener('product-form:cart:navigate', (event) => {
        event.preventDefault();
        if (event.detail?.cart) this.cart = event.detail.cart;
        this.open(document.activeElement);
        this.refresh();
      }, { signal });
      document.addEventListener('cart:open', () => { this.open(); this.refresh(); }, { signal });
      document.addEventListener('cart:close', () => this.close(), { signal });
      document.addEventListener('cart:build', () => this.refresh(), { signal });
      document.addEventListener('cart:updated', (event) => {
        if (event.detail?.source === 'cart-drawer') return;
        if (event.detail?.cart) this.cart = event.detail.cart;
        this.updateCount();
        this.refresh();
      }, { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) { this.close(); this.controller.abort(); }
      }, { signal });
    }

    merchandiseItems() {
      return (this.cart?.items || []).filter((item) => item.sku !== 'x-redo' && item.vendor !== 're:do');
    }

    updateCount() {
      const count = this.merchandiseItems().reduce((sum, item) => sum + item.quantity, 0);
      document.querySelectorAll('[data-header-cart-count], .cart-link__bubble-num, [data-cart-count]').forEach((element) => {
        element.textContent = count;
        if (element.matches('[data-header-cart-count]')) element.hidden = count === 0;
      });
      document.querySelectorAll('.cart-link__bubble').forEach((element) => element.classList.toggle('cart-link__bubble--visible', count > 0));
    }

    open(opener) {
      if (this.hasAttribute('open')) return;
      this.previouslyFocused = opener || document.activeElement;
      if (this.previouslyFocused?.closest('.modal')) this.previouslyFocused = document.querySelector('[data-cart-drawer-open]');
      document.dispatchEvent(new CustomEvent('drawerOpen'));
      document.dispatchEvent(new CustomEvent('drawerOpen.CartDrawer'));
      this.scrollPosition = window.scrollY;
      this.savedBody = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width };
      this.setAttribute('open', '');
      this.panel.inert = false;
      this.panel.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');
      Object.assign(document.body.style, { position: 'fixed', top: `-${this.scrollPosition}px`, width: '100%' });
      document.querySelectorAll('[data-cart-drawer-open], .js-drawer-open-cart').forEach((element) => element.setAttribute('aria-expanded', 'true'));
      this.querySelector('.cart-drawer__close').focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('cart-drawer:opened', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('redo:re-render-elements'));
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.panel.inert = true;
      this.panel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-open');
      Object.assign(document.body.style, this.savedBody);
      window.scrollTo(0, this.scrollPosition);
      document.querySelectorAll('[data-cart-drawer-open], .js-drawer-open-cart').forEach((element) => element.setAttribute('aria-expanded', 'false'));
      if (this.previouslyFocused?.isConnected) this.previouslyFocused.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (!this.hasAttribute('open')) return;
      if (event.key === 'Enter' && event.target.matches('[data-cart-quantity]')) {
        event.preventDefault();
        this.onChange(event);
        return;
      }
      if (window.CartDrawerRedo?.isInfoDialogOpen()) return;
      const path = event.composedPath();
      const inPanel = path.includes(this.panel);
      if (event.key === 'Escape') {
        if (!inPanel && event.target !== document.body) return;
        event.preventDefault();
        this.close();
      }
      if (event.key !== 'Tab') return;
      const focusable = window.CartDrawerRedo?.getFocusableElements(this.panel) || Array.from(this.panel.querySelectorAll('a[href], button, input, textarea, select, [tabindex]')).filter((node) => !node.disabled && node.tabIndex >= 0 && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = window.CartDrawerRedo?.getActiveElement() || path[0];
      if (!inPanel) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      } else if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    onClick(event) {
      if (event.target.closest('[data-cart-close]')) this.close();
      const button = event.target.closest('[data-cart-adjust], [data-cart-remove]');
      if (!button || this.busy) return;
      const row = button.closest('[data-cart-key]');
      const quantity = Number(row.querySelector('[data-cart-quantity]').dataset.quantity);
      const next = button.hasAttribute('data-cart-remove') ? 0 : Math.max(0, quantity + Number(button.dataset.cartAdjust));
      this.changeQuantity(row.dataset.cartKey, next, button);
    }

    onChange(event) {
      if (event.target.matches('[data-cart-quantity]')) {
        const input = event.target;
        const quantity = Number(input.value.trim());
        if (this.busy || !/^\d+$/.test(input.value.trim()) || !Number.isSafeInteger(quantity)) {
          input.value = input.dataset.quantity;
          return;
        }
        this.changeQuantity(input.closest('[data-cart-key]').dataset.cartKey, quantity, input);
      }
      if (event.target.name === 'note') {
        this.updateNote(event.target.value);
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.panel.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-cart-adjust], [data-cart-remove], [data-cart-quantity], [name="checkout"]').forEach((element) => { element.disabled = busy; });
      this.querySelector('[data-cart-checkout]').inert = busy;
    }

    showError(error) {
      this.message.textContent = typeof error?.description === 'string' ? error.description : this.dataset.errorMessage;
      this.message.hidden = false;
    }

    async updateNote(note) {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note })
        });
        if (!response.ok) throw new Error();
      } catch (error) { this.showError(error); }
    }

    async changeQuantity(key, quantity, control) {
      this.refreshVersion += 1;
      this.setBusy(true);
      this.message.hidden = true;
      const action = control.hasAttribute('data-cart-remove') ? '[data-cart-remove]' : control.hasAttribute('data-cart-adjust') ? `[data-cart-adjust="${control.dataset.cartAdjust}"]` : '[data-cart-quantity]';
      try {
        const response = await fetch(this.dataset.changeUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: [this.dataset.sectionId], sections_url: this.dataset.cartUrl })
        });
        const cart = await response.json();
        if (!response.ok) throw cart;
        this.cart = cart;
        if (!this.renderSection(cart.sections?.[this.dataset.sectionId])) await this.refresh(true);
        this.updateCount();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'cart-drawer' } }));
        window.dispatchEvent(new CustomEvent('11six24:cart-updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('redo:reload-cart'));
        document.dispatchEvent(new CustomEvent('redo:re-render-elements'));
      } catch (error) {
        this.showError(error);
        await this.refresh(false, true);
      } finally {
        this.setBusy(false);
        const row = Array.from(this.querySelectorAll('[data-cart-key]')).find((element) => element.dataset.cartKey === key);
        (row?.querySelector(action) || this.querySelector('.cart-drawer__close')).focus({ preventScroll: true });
      }
    }

    async refresh(throwOnError = false, keepError = false) {
      if (this.busy && !throwOnError && !keepError) return;
      const version = ++this.refreshVersion;
      try {
        const url = new URL(this.dataset.cartUrl, location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
        if (!response.ok) throw new Error();
        const html = await response.text();
        if (version !== this.refreshVersion) return;
        if (!this.renderSection(html)) throw new Error();
        if (!keepError) this.message.hidden = true;
      } catch (error) {
        if (throwOnError) throw error;
        this.showError(error);
      }
    }

    renderSection(html) {
      if (!html) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('cart-drawer');
      if (!next) return false;
      const cartJSON = next.querySelector('[data-cart-drawer-cart]');
      if (cartJSON) this.cart = JSON.parse(cartJSON.textContent);
      const items = this.querySelector('[data-cart-items]');
      const scroll = items.scrollTop;
      const focused = document.activeElement;
      const focusedId = items.contains(focused) ? focused.id : null;
      ['[data-cart-shipping]', '[data-cart-items]', '[data-cart-summary]'].forEach((selector) => {
        this.querySelector(selector).replaceWith(next.querySelector(selector));
      });
      this.querySelector('[data-cart-items]').scrollTop = scroll;
      if (focusedId) (document.getElementById(focusedId) || this.querySelector('.cart-drawer__close')).focus({ preventScroll: true });
      this.querySelector('[data-cart-footer]').hidden = !this.merchandiseItems().length;
      this.updateCount();
      this.dispatchEvent(new CustomEvent('cart-drawer:rendered', { bubbles: true, detail: { cart: this.cart } }));
      return true;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
